import React from 'react';

export default function HeaderBar({
    appName = "QuickRoute",
    uiMode, setUiMode,
    activePlaybook, setActivePlaybook,
    playbooks = [],
    searchTerm, setSearchTerm,
    onSavePlay, onExportImage,
    playbackState, togglePlay,
    onRequestModal,
    currentPlayName, onPrevPlay, onNextPlay, hasPlays,
    theme, setTheme, // Theme Props
    viewMode, setViewMode, // View Mode Props
    canUndo, canRedo, onUndo, onRedo, // Undo System
    isLocked, setIsLocked, // Lock System
    onExit, // Exit to Dashboard
    onShare, // Share Playbook (Cloud only)
    onOpenVideoLibrary,
    videoCount = 0,
    saveStatus, lastSaved, isCloud, isShared, // Sync Support
    labelMode, setLabelMode, // Label Mode Support
    onOpenPrint, // Print Feature Support
    isSinglePlayMode = false // Restriction for shared single plays
}) {

    return (
        <header className="qr-header flex justify-between items-center px-4 py-2 border-b border-gray-700 bg-gray-900 select-none">

            {/* LEFT SECTION */}
            <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                    <img
                        src="/logo.png"
                        alt="QuickRoute"
                        className="h-10 w-auto"
                        style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
                    />
                </div>

                <div className="flex bg-gray-800 rounded p-1">
                    {!isSinglePlayMode && onExit && (
                        <>
                            <button
                                title="Return to Playbook Dashboard"
                                className="px-4 py-1.5 rounded text-sm font-medium text-gray-400 hover:text-white transition-colors"
                                onClick={onExit}
                            >
                                Dashboard
                            </button>
                            <div className="h-4 w-px bg-gray-700 mx-1 self-center"></div>
                        </>
                    )}
                    {!isSinglePlayMode && (
                        <button
                            title="View and Manage Play Library"
                            className={`px-5 py-1.5 rounded text-sm font-bold transition-all shadow-sm ${uiMode === 'library' ? 'bg-gray-700 text-white ring-1 ring-gray-600' : 'text-gray-400 hover:text-white hover:bg-gray-700/50'}`}
                            onClick={() => setUiMode('library')}
                        >
                            Quick Routes
                        </button>
                    )}
                    <button
                        title="Design and Edit Plays"
                        className={`px-5 py-1.5 rounded text-sm font-bold transition-all shadow-sm ${uiMode === 'builder' ? 'bg-blue-600 text-white shadow-blue-500/20 ring-1 ring-blue-500' : 'text-gray-400 hover:text-white hover:bg-gray-700/50'}`}
                        onClick={() => setUiMode('builder')}
                    >
                        Builder
                    </button>
                </div>

                <div className="h-6 w-px bg-gray-700 mx-2"></div>

                {/* Playbook Picker */}
                {!isSinglePlayMode && (
                    <div className="flex items-center gap-2">
                        <div className="relative group">
                            <select
                                className="bg-gray-800 text-white text-sm rounded border border-gray-600 px-3 py-1.5 pr-8 appearance-none hover:border-gray-500 cursor-pointer outline-none focus:border-blue-500"
                                value={activePlaybook}
                                onChange={(e) => setActivePlaybook(e.target.value)}
                            >
                                {playbooks.map(pb => <option key={pb} value={pb}>{pb}</option>)}
                            </select>
                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-[10px] text-gray-400">▼</div>
                        </div>

                        {/* Theme Picker */}
                        <div className="relative group ml-1">
                            <select
                                className="bg-gray-800 text-white text-sm rounded border border-gray-600 px-3 py-1.5 pr-8 appearance-none hover:border-gray-500 cursor-pointer outline-none focus:border-blue-500"
                                value={theme}
                                onChange={(e) => setTheme(e.target.value)}
                            >
                                <option value="modern">Modern</option>
                                <option value="classic">Classic</option>
                                <option value="white">Print</option>
                            </select>
                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-[10px] text-gray-400">▼</div>
                        </div>

                        {/* View Mode Toggle */}
                        <div className="flex bg-gray-800 rounded p-1 ml-2">
                            <button
                                title="Portrait View (Vertical)"
                                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${viewMode === 'portrait' ? 'bg-gray-600 text-white shadow-sm' : 'text-gray-400 hover:text-white'}`}
                                onClick={() => setViewMode('portrait')}
                            >
                                📱
                            </button>
                            <button
                                title="Landscape View (Wide)"
                                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${viewMode === 'landscape' ? 'bg-gray-600 text-white shadow-sm' : 'text-gray-400 hover:text-white'}`}
                                onClick={() => setViewMode('landscape')}
                            >
                                🖥️
                            </button>
                        </div>

                        <div className="flex gap-1 ml-1">
                            {onRequestModal && (
                                <>
                                    <button
                                        title="New Playbook"
                                        className="text-gray-400 hover:text-green-400 text-lg px-1.5"
                                        onClick={() => onRequestModal('create-playbook', '')}
                                    >+</button>
                                    <button title="Rename Playbook" className="text-gray-400 hover:text-blue-400 text-sm px-1.5" onClick={() => onRequestModal('rename-playbook', activePlaybook)}>✎</button>
                                </>
                            )}
                        </div>
                    </div>
                )}

                <div className="h-6 w-px bg-gray-700 mx-2"></div>

                {/* Label Mode Toggle */}
                {setLabelMode && (
                    <div className="relative group">
                        <select
                            className="bg-gray-800 text-white text-[10px] uppercase font-bold tracking-wider rounded border border-gray-600 px-2 py-1 pr-6 appearance-none hover:border-gray-500 cursor-pointer outline-none focus:border-blue-500"
                            value={labelMode}
                            onChange={(e) => setLabelMode(e.target.value)}
                            title="Player Label Mode"
                        >
                            <option value="simple">Simple Labels</option>
                            <option value="coaching">Coaching Tags</option>
                            <option value="numbers">Numbers (C, QB, 1-5)</option>
                            <option value="user">User Custom</option>
                        </select>
                        <div className="absolute right-1 top-1/2 transform -translate-y-1/2 pointer-events-none text-[8px] text-gray-400">▼</div>
                    </div>
                )}
            </div>

            {/* CENTER SECTION */}
            <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center justify-center">
                {uiMode === 'library' ? (
                    <input
                        type="text"
                        placeholder="Search plays..."
                        className="bg-gray-800 border border-gray-600 rounded px-4 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500 w-64 transition-all"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                ) : (
                    <div className="flex items-center bg-gray-800/40 border border-gray-700/50 rounded-full px-1.5 py-1 w-[320px] shadow-inner">
                        {!isSinglePlayMode && (
                            <button
                                onClick={onPrevPlay}
                                disabled={!hasPlays}
                                className="w-8 h-8 flex-shrink-0 flex items-center justify-center text-gray-400 hover:text-white disabled:opacity-10 rounded-full hover:bg-gray-700 transition-all active:scale-90"
                                title="Previous Play"
                            >
                                <span className="text-[10px]">◀</span>
                            </button>
                        )}
                        <div
                            className="flex-1 text-center truncate text-white font-bold text-sm px-2 select-text"
                            title={currentPlayName || 'New Play'}
                        >
                            {currentPlayName || 'New Play'}
                        </div>
                        {!isSinglePlayMode && (
                            <button
                                onClick={onNextPlay}
                                disabled={!hasPlays}
                                className="w-8 h-8 flex-shrink-0 flex items-center justify-center text-gray-400 hover:text-white disabled:opacity-10 rounded-full hover:bg-gray-700 transition-all active:scale-90"
                                title="Next Play"
                            >
                                <span className="text-[10px]">▶</span>
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* RIGHT SECTION */}
            <div className="flex items-center gap-3">
                {uiMode === 'builder' && (
                    <>
                        <div className="flex bg-gray-800 rounded p-1 mr-2 gap-1 px-2 items-center">
                            <button
                                title={isLocked ? "Unlock Play (Enable editing)" : "Lock Play (Prevent moving tokens)"}
                                className={`p-1.5 rounded transition-colors ${isLocked ? 'bg-red-900/40 text-red-400 hover:bg-red-800/60' : 'text-gray-400 hover:text-white'}`}
                                onClick={() => setIsLocked(!isLocked)}
                            >
                                {isLocked ? '🔒' : '🔓'}
                            </button>
                            <div className="w-px h-4 bg-gray-700 mx-1"></div>
                            <button
                                title="Undo (Ctrl+Z)"
                                className="px-2 py-1.5 text-gray-400 hover:text-white disabled:opacity-30"
                                onClick={onUndo}
                                disabled={!canUndo}
                            >
                                ↩️
                            </button>
                            <button
                                title="Redo (Ctrl+Y)"
                                className="px-2 py-1.5 text-gray-400 hover:text-white disabled:opacity-30"
                                onClick={onRedo}
                                disabled={!canRedo}
                            >
                                ↪️
                            </button>
                        </div>
                        {onShare && (
                            <button
                                title="Collaborate & Share"
                                className="bg-green-600 hover:bg-green-500 text-white px-4 py-1.5 rounded font-bold text-sm shadow transition-all hover:shadow-green-500/30 active:scale-95 flex items-center gap-2"
                                onClick={onShare}
                            >
                                <span>👥</span> Share
                            </button>
                        )}
                        {onOpenVideoLibrary && (
                            <button
                                title="Open Video Library"
                                className="bg-gray-800 hover:bg-gray-700 text-blue-100 px-3 py-1.5 rounded font-bold text-sm border border-blue-800/60 transition-all active:scale-95 flex items-center gap-2"
                                onClick={onOpenVideoLibrary}
                            >
                                <span>Video</span>
                                {videoCount > 0 && (
                                    <span className="bg-blue-600/30 border border-blue-500/40 text-blue-100 text-[10px] rounded-full px-1.5 py-0.5 leading-none">
                                        {videoCount}
                                    </span>
                                )}
                            </button>
                        )}
                        {onSavePlay && (
                            <button
                                title="Save all changes to the current play (Auto-saves periodically)"
                                className={`px-4 py-1.5 rounded font-bold text-sm shadow transition-all active:scale-95 flex items-center gap-2 ${saveStatus === 'saving' ? 'bg-blue-700 cursor-wait' : 'bg-blue-600 hover:bg-blue-500 hover:shadow-blue-500/30'}`}
                                onClick={onSavePlay}
                                disabled={saveStatus === 'saving'}
                            >
                                {saveStatus === 'saving' ? (
                                    <>
                                        <svg className="animate-spin h-3 w-3 text-white" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Saving...
                                    </>
                                ) : 'Save'}
                            </button>
                        )}
                        <button
                            title="Download play as a high-resolution image"
                            className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-1.5 rounded font-bold text-sm border border-gray-600 transition-all active:scale-95"
                            onClick={onExportImage}
                        >
                            Export
                        </button>
                        <button
                            title="Print Play (PDF / Wristband inserts)"
                            className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded font-bold text-sm border border-gray-600 flex items-center gap-2 transition-all active:scale-95"
                            onClick={onOpenPrint}
                        >
                            <span>🖨️</span> Print
                        </button>
                        <button
                            title={playbackState.isPlaying ? "Stop Animation" : "Play Animation (Space)"}
                            className={`px-4 py-1.5 rounded font-bold text-sm border transition-all active:scale-95 ${playbackState.isPlaying ? 'bg-red-900 border-red-700 text-red-100 hover:bg-red-800' : 'bg-blue-900/40 border-blue-800 text-blue-200 hover:bg-blue-900/60'}`}
                            onClick={togglePlay}
                        >
                            {playbackState.isPlaying ? 'Stop' : 'Play'}
                        </button>
                    </>
                )}
            </div>
        </header>
    );
}
